import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ScrollView, Sheet, Stack, Text, View, XStack, YStack } from "tamagui";
import { RiCloseFill } from "react-icons/ri";
import { appToast } from "@components/Toast";
import { handleAPIRegisterPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";
import KioskIcon from "./KioskIcon";
import { kiosk } from "./kioskTheme";
import { TAPPAY_SERVER_TYPE } from "@utils/tappaySdk";

const TAPPAY_SCRIPT_URL = process.env.REACT_APP_TAPPAY_SCRIPT_URL || "";
const APP_ID = Number(process.env.REACT_APP_TAPPAY_APP_ID) || 0;
const APP_KEY = process.env.REACT_APP_TAPPAY_APP_KEY || "";

// declare global Window.TPDirect 已集中宣告於 @utils/tappaySdk.ts，此處不再重複宣告。

/** 卡欄位外框（內含 TapPay Fields 掛載點）。 */
const FieldBox = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <YStack backgroundColor="#f4f6fb" borderRadius={14} p={14} gap={7}>
    <Text fontSize={12} color={kiosk.grey}>
      {label}
    </Text>
    {children}
  </YStack>
);

/**
 * 新增信用卡 modal（tamagui Sheet）。TapPay Fields 真卡輸入 → getPrime → 註冊 token。
 * onClose：關閉；onBound：綁卡成功後回呼，帶回新建的付款方式（父層可重新載入清單並自動選中）；cusJwtToken：註冊用。
 */
const KioskAddCard = ({
  onClose,
  onBound,
  cusJwtToken,
}: {
  onClose?: () => void;
  onBound?: (created?: { payment_method_id?: string; card?: { lastfour?: string } }) => void;
  cusJwtToken?: string;
} = {}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [agree, setAgree] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [cardStatus, setCardStatus] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const inited = useRef(false);

  const close = () => {
    setOpen(false);
    if (onClose) onClose();
    else navigate(-1);
  };

  // 載入 TapPay SDK 並把 Fields 掛到 modal 內的元素（Sheet 動畫進場後元素才在 DOM，故輪詢直到成功）。
  useEffect(() => {
    let timer: any;
    const tryInit = () => {
      if (inited.current) return true;
      const el = document.getElementById("kiosk-card-number");
      if (!window.TPDirect || !el) return false;
      inited.current = true;
      window.TPDirect.setupSDK(APP_ID, APP_KEY, TAPPAY_SERVER_TYPE);
      window.TPDirect.card.setup({
        fields: {
          number: { element: "#kiosk-card-number", placeholder: "**** **** **** ****" },
          expirationDate: { element: "#kiosk-card-expiration-date", placeholder: "MM / YY" },
          ccv: { element: "#kiosk-card-ccv", placeholder: "CCV" },
        },
        styles: { input: { color: kiosk.ink, "font-size": "16px", "font-weight": "700" }, "input.valid": { color: kiosk.green }, "input.invalid": { color: kiosk.danger } },
      });
      window.TPDirect.card.onUpdate((u: any) => setCardStatus(u));
      setSdkReady(true);
      return true;
    };

    const startPolling = () => {
      if (tryInit()) return;
      timer = setInterval(() => {
        if (tryInit()) clearInterval(timer);
      }, 150);
    };

    if (window.TPDirect) {
      startPolling();
    } else if (TAPPAY_SCRIPT_URL) {
      let script = document.querySelector(`script[src="${TAPPAY_SCRIPT_URL}"]`) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.src = TAPPAY_SCRIPT_URL;
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener("load", startPolling, { once: true });
      // 已載入但事件錯過 → 保底輪詢
      startPolling();
    }
    return () => timer && clearInterval(timer);
  }, []);

  const canBind = agree && sdkReady && cardStatus?.canGetPrime && !submitting;

  const bind = () => {
    if (!window.TPDirect || !canBind) return;
    setSubmitting(true);
    window.TPDirect.card.getPrime(async (result: any) => {
      if (result.status !== 0) {
        setSubmitting(false);
        appToast.error(`取得卡片資訊失敗：${result.msg || "請確認卡號"}`);
        return;
      }
      const prime = result?.card?.prime;
      const last4 = result?.card?.lastfour ?? "";
      const reg = await handleAPIRegisterPaymentMethod(
        { method_type: "credit_card", prime, nickname: `**** **** **** ${last4}` },
        cusJwtToken || "",
      );
      setSubmitting(false);
      if (reg.state === "success") {
        // 3DS challenge：跳出到銀行驗證頁；完成後由落地頁（Task 9）處理導回。
        if (reg.data?.bind_status === "challenge_required" && reg.data.payment_url) {
          window.location.href = reg.data.payment_url;
          return;
        }
        appToast.success("已新增信用卡");
        if (onBound) onBound(reg.data?.payment_method ?? undefined);
        close();
      } else {
        const msg = Array.isArray(reg.error) ? reg.error.join("\n") : reg.error;
        appToast.error(msg || "綁卡失敗，請稍後再試");
      }
    });
  };

  return (
    <Sheet
      modal
      open={open}
      onOpenChange={(o: boolean) => {
        if (!o) close();
      }}
      snapPoints={[92]}
      dismissOnSnapToBottom
      animation="medium">
      <Sheet.Overlay animation="lazy" enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} backgroundColor="rgba(0,0,0,0.45)" />
      <Sheet.Handle />
      <Sheet.Frame bg="#fff" borderTopLeftRadius={24} borderTopRightRadius={24}>
        <XStack ai="center" jc="space-between" px="$5" pt="$4" pb="$2">
          <Text fontSize={20} fontWeight="800" color={kiosk.ink}>
            新增信用卡
          </Text>
          <Button p={4} chromeless onPress={close} pressStyle={{ opacity: 0.5 }}>
            <RiCloseFill size={22} color={kiosk.grey} />
          </Button>
        </XStack>

        <ScrollView px="$4" showsVerticalScrollIndicator={false}>
          <YStack gap={12} pb="$4">
            <Text fontSize={13} color={kiosk.grey} mb={2}>
              綁定後可在結帳時選用
            </Text>
            <FieldBox label="卡號">
              <View id="kiosk-card-number" h={24} />
            </FieldBox>
            <XStack gap={12}>
              <Stack flex={1}>
                <FieldBox label="有效期限">
                  <View id="kiosk-card-expiration-date" h={24} />
                </FieldBox>
              </Stack>
              <Stack width={130}>
                <FieldBox label="安全碼">
                  <View id="kiosk-card-ccv" h={24} />
                </FieldBox>
              </Stack>
            </XStack>
            <XStack ai="center" gap={6}>
              <KioskIcon name="check" size={13} color={kiosk.green} strokeWidth={2.6} />
              <Text fontSize={11.5} color={kiosk.grey}>
                由 TapPay 安全加密，卡號不經過本商店
              </Text>
            </XStack>

            {/* 同意自動扣款 */}
            <XStack mt={10} ai="flex-start" gap={10} onPress={() => setAgree(!agree)} pressStyle={{ opacity: 0.7 }} cursor="pointer">
              <Stack width={22} height={22} borderRadius={6} borderWidth={2} borderColor={agree ? kiosk.blue : "#cfd6e4"} backgroundColor={agree ? kiosk.blue : "#fff"} ai="center" jc="center" mt={1}>
                {agree ? <KioskIcon name="check" size={13} color="#fff" strokeWidth={2.8} /> : null}
              </Stack>
              <Text flex={1} fontSize={14} color={kiosk.ink} lineHeight={20}>
                我同意訂單送達後，以此卡自動扣款
              </Text>
            </XStack>
          </YStack>
        </ScrollView>

        <YStack px="$4" pt="$3" pb="$6">
          <Button
            h={52}
            width="100%"
            borderRadius={14}
            backgroundColor={canBind ? kiosk.blue : "#e2e6ee"}
            disabled={!canBind}
            onPress={bind}
            pressStyle={{ opacity: 0.9 }}
            hoverStyle={{ backgroundColor: canBind ? kiosk.blueD : "#e2e6ee" }}>
            <Text color={canBind ? "#fff" : kiosk.grey} fontSize={16} fontWeight="800">
              {submitting ? "處理中…" : "綁定卡片"}
            </Text>
          </Button>
        </YStack>
      </Sheet.Frame>
    </Sheet>
  );
};

export default KioskAddCard;
